import React, { useState, useEffect, useCallback } from "react";
import CustomButton from "../../../atoms/CustomButton";
import Import from "../../../icons/Import";
import Filter from "../../../icons/Filter";
import CustomDataGrid from "../../../organisms/CustomDatagrid";
import Modal from "../../../organisms/modals/Modal";
import CustomMenu from "../../../atoms/CustomMenu";
import { v4 as uuidv4 } from "uuid";
import View from "../../../icons/View";
import Edit from "../../../icons/Edit";
import Delete from "../../../icons/Delete";
import VerticalMenu from "../../../icons/VerticalMenu";
import { getData, deleteData } from "../../../../services";
import { useCookies } from "react-cookie";
import Form from "./Form";
import Pagination from "../../../molecules/Pagination";
import DeleteModal from "../../../organisms/modals/DeleteModal";
import Search from "../../../molecules/search";
import { getEmployeeList } from "../../../../services/basicGets";
import NoContentCard from "../../../atoms/NoContentCard";
import NoContentIcon from "../../../icons/NocontentIcon";
import toast from "react-hot-toast";
import {
  errorStyles,
  confimationStyles,
} from "../../../../assets/styles/toast";
import { Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import RemarkForm from "../TeamStatusReport/RemarkForm";

const TeamManagement = () => {
  const [cookies] = useCookies([]);
  const [selectedRow, setSelectedRow] = useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [searchedValue, setSearchedValue] = useState();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [employeeData, setEmployeeData] = useState([]);
  const [modalState, setModalState] = useState({
    isModalOpen: false,
    modalData: null,
    isDeleteModalOpen: false,
    modalId: "",
    name: "",
  });

  //access details handling
  const accessData = useSelector((state) => state.accessDetails?.accessData);
  const teamAccess =
    accessData?.access?.find((module) => module.module === "Team Management") ||
    {};
  const isAdmin = accessData?.role?.label === "Admin";

  const canCreate = isAdmin || teamAccess?.is_create;
  const canEdit = isAdmin || teamAccess?.is_edit;
  const canDelete = isAdmin || teamAccess?.is_delete;

  const getTeamData = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await getData({
        endpoint: "Team/GetAllTeams",
        params: {
          searchQuery: searchedValue,
          page: currentPage,
          limit: itemsPerPage,
          employee_id : isAdmin ? null : cookies.euid
        },

        token: cookies.e_t,
      });

      if (res) {
        setTableData(res?.data);
      }
    } catch (error) {
      console.error(error); // Use console.error for errors
    } finally {
      setIsLoading(false)
    }
  }, [cookies.e_t, currentPage, itemsPerPage, searchedValue]);

  useEffect(() => {
    (async () => {
      const resEmployee = await getEmployeeList({
        token: cookies.e_t,
      });

      setEmployeeData(resEmployee?.employeeResponseValueLabel);
    })();
  }, []);

  useEffect(() => {
    getTeamData();
  }, [getTeamData]);

  //handling menu
  const menu = [
    canEdit && {
      icon: <Edit height="20" width="20" />,
      label: "Edit",
      onClick: () => {
        setAnchorEl(null);
        handleEdit(selectedRow);
      },
    },
    canDelete && {
      icon: <Delete />,
      label: "Delete",
      onClick: () => {
        setAnchorEl(null);
        handleDeleteModal(selectedRow);
      },
    },
  ].filter(Boolean);

  const handleDeleteModal = (data) => {
    setModalState((prevState) => ({
      ...prevState,
      isDeleteModalOpen: true,
      modalId: data.id,
      name: data.team_name,
      email: data.email,
    }));
  };

  const handleDeletion = async () => {
    try {
      await deleteData({
        endpoint: "Team/deleteTeam",
        params: {
          team_id: selectedRow.team_id,
        },
      });
      toast.success("Team Deleted Sucessfully", {
        style: confimationStyles,
        duration: 1000,
      });
      getTeamData();
      setModalState((prevState) => ({
        ...prevState,
        isDeleteModalOpen: false,
      }));
    } catch (error) {
      toast.error("An Error Occured While Deleting", {
        style: errorStyles,
        duration: 1000,
      });
      console.log(error);
    }
  };

  const handleStatusMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((perPage) => {
    setCurrentPage(1);
    setItemsPerPage(perPage);
  }, []);

  const handleEdit = (tableData) => {
    setModalState((prevState) => ({
      ...prevState,
      modalId: tableData.id,
      modalData: tableData,
      isModalOpen: true,
      title: "Edit Team",
    }));
  };

  const columns = [
    {
      field: "team_name",
      headerName: "Team Name",
      flex: 1,
      sortable: true,
    },
    {
      field: "team_lead_name",
      headerName: "Team Lead",
      flex: 1,
      sortable: true,
    },

    {
      field: "employees",
      headerName: "Members",
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const employees = params.row.employees || [];

        // Get all employee names
        const employeeNames = employees
          .map((emp) => emp.employee_name)
          .filter((name) => name?.trim()); // Filter out empty names

        // Show first two and the count of remaining
        const displayText =
          employeeNames.length > 2
            ? `${employeeNames.slice(0, 2).join(", ")} +${
                employeeNames.length - 2
              }`
            : employeeNames.join(", ");

        return (
          <Tooltip title={employeeNames.join(", ")} arrow>
            <span
              style={{
                cursor: "pointer",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {displayText}
            </span>
          </Tooltip>
        );
      },
    },

    {
      field: "team_description",
      headerName: "Description",
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <span className="multiline-ellipsis">
            {params.value}
          </span>
        </Tooltip>
      )
    },
    // {
    //   field: "Created On",
    //   headerName: "Status",
    //   flex: 1,
    //   sortable: true,
    // },
    ...(canEdit || canDelete
      ? [
          {
            field: "actions",
            headerName: "",
            flex: 0.2,
            renderCell: (params) => {
              return (
                <div className="table-icons-container">
                  <div onClick={(event) => handleStatusMenu(event, params.row)}>
                    <VerticalMenu />
                  </div>

                  <CustomMenu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    items={menu}
                  />
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const openModal = (title, data) => {
    const modalId = uuidv4();
    setModalState((prevState) => ({
      ...prevState,
      modalId,
      isModalOpen: true,
      title,
      modalData: data,
    }));
  };

  const closeModal = () => {
    // if(type === 'submitted'){
    setModalState((prevState) => ({ ...prevState, isModalOpen: false }));
    // } else {
    //   // setModalState((prevState) => ({ ...prevState, isModalOpen: false }));
    //   setIsFormDirty(true)
    //   setModalState((prevState) => ({ ...prevState, isModalOpen: false }));
    // }
  };

  const CustomNoRowsOverlay = React.memo(() => {
    return (
      <>
        <NoContentCard title="No Teams Found" type="Teams" />
      </>
    );
  });

  return (
    <div id="Team-Management" className="main-container">
      <div className="d-flex justify-content-between">
        <div className="d-flex gap-2">
          <Search
            placeholder="Search"
            onSearchEmit={(item) => {
              if (currentPage !== 1) {
                setCurrentPage(1);
              }
              setSearchedValue(item);
            }}
          />
        </div>
        <div className="d-flex gap-2">
          {/* <CustomButton
            text="Import"
            type="btn-outline-primary"
            icon={<Import />}
          /> */}
          {canCreate && (
            <CustomButton
              text="Create Team"
              type="btn-primary"
              handleClick={() => openModal("Create Team")}
            />
          )}
        </div>
      </div>
      <div className="mt-3">
        <CustomDataGrid
          columns={columns}
          CustomNoRowsOverlay={CustomNoRowsOverlay}
          rows={tableData?.teams || []}
          getRowId={(row) => row.team_id}
          isLoadingStateProvided={true}
          isLoading={isLoading}
          onCellClick={(params) => {
            if (params.field !== "actions" && canEdit) {
              handleEdit(params.row);
            }
          }}
          canEdit={canEdit}
        />
      </div>



      {tableData?.total_records > 10 && (
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={tableData?.total_records}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      {modalState?.isModalOpen && (
        <Modal
          id={modalState?.modalId}
          title={modalState.title}
          isActive={modalState?.isModalOpen}
          onClose={closeModal}
          takeConfirmation={isFormDirty ? true : false}
          width="600px"
          extrastyle={{ overflow: "visible" }}
        >
          <Form
            onFormDirty={setIsFormDirty}
            initialData={
              modalState.title === "Edit Team" ? modalState.modalData : null
            }
            refetchData={getTeamData}
            onClose={() => closeModal("submit")}
            employeeList={employeeData}
            isAdmin={isAdmin}
          />
        </Modal>
      )}

      {modalState.isDeleteModalOpen && (
        <DeleteModal
          name={modalState.name}
          id={modalState.modalId}
          isActive={modalState.isDeleteModalOpen}
          onClose={() =>
            setModalState((prevState) => ({
              ...prevState,
              isDeleteModalOpen: false,
            }))
          }
          title="Team"
          onClick={handleDeletion}
        />
      )}
    </div>
  );
};

export default TeamManagement;
